import React, {useEffect, useState} from 'react';
import {Button, Input, Table, message, Modal, Checkbox, Tooltip, Row, Col} from 'antd';
import {
    HistoryOutlined,
    DeleteOutlined,
    EditOutlined,
    PlayCircleOutlined,
    VideoCameraOutlined,
    DownloadOutlined,
    UploadOutlined, EyeOutlined
} from '@ant-design/icons';
import {
    TestDto,
    TestControllerApi,
    TestResultDto,
    TestExecutionControllerApi,
    PageTestDto,
    Pageable,
    ExecuteTestRequest, DeletionError, ExportTestControllerApi, TestExecutionDto
} from "../api";
import {Link} from "react-router-dom";
import TestResultModal from "../components/modal/testExecution/TestResultModal";
import TestExecutionModal from "../components/modal/testExecution/TestExecutionModal";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import DeletionErrorModal from "../components/modal/error/DeletionErrorModal";
import {AlignType} from "rc-table/lib/interface";
import {downloadFile} from "../utils/FileDownload";
import ImportTestModal from "../components/import/ImportTestModal";
import axios from "axios";
import {CustomPlaywrightApi, TestRequest} from "../api-custom/api";

const TestOverview = ({system}) => {
    const {keycloak} = useKeycloak();
    const [testData, setTestData] = useState<TestDto[]>();
    const [searchText, setSearchText] = useState<string>(''); // Der Suchbegriff
    const [isLoading, setIsLoading] = useState<number | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteTest, setDeleteTest] = useState<TestDto>();
    const [delayValues, setDelayValues] = useState({});
    const [recordingStates, setRecordingStates] = useState({});
    const [headlessStates, setHeadlessStates] = useState({});
    const [testResultModalVisible, setTestResultModalVisible] = useState(false);
    const [testResult, setTestResult] = useState<TestResultDto | null>(null);
    const [testIdForRun, setTestIdForRun] = useState<number | null>(null);
    const [runHistoryVisible, setRunHistoryVisible] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [selectedTests, setSelectedTests] = useState<number[]>([]);
    const [localTestResult, setLocalTestResult] = useState<boolean>(false);
    const [localTraceUrl, setLocalTraceUrl] = useState<string | undefined>(undefined);

    //Import Modal von Tests
    const [isModalForImportTestVisible, setIsModalForImportTestVisible] = useState<boolean>(false);

    // Für das Fehlermeldungsmodal
    const [deletionErrorList, setDeletionErrorList] = useState<DeletionError[]>([]);
    const [deletionModalVisible, setDeletionModalVisible] = useState<boolean>(false);
    const errorMessage = "Dieser Test wird noch in folgenden Testplänen verwendet. Bitte lösche diese oder entferne den Test aus dem Testplan";

    const handleCloseDeletionModal = () => {
        setDeletionModalVisible(false);
    }
    //

    const handleDelayValues = (recordId, value) => {
        setDelayValues((prevInputValues) => ({
            ...prevInputValues,
            [recordId]: value,
        }));
    };

    const handleRecordCheckboxChange = (checked, recordId) => {
        setRecordingStates(prevStates => ({
            ...prevStates,
            [recordId]: checked,
        }));
    };

    const handleHeadlessCheckboxChange = (checked, recordId) => {
        setHeadlessStates(prevStates => ({
            ...prevStates,
            [recordId]: checked,
        }));
    };

    const headers = useKeycloakHeader();
    const testControllerApi = new TestControllerApi();
    const testExecutionControllerApi = new TestExecutionControllerApi();
    const exportTestControllerApi = new ExportTestControllerApi();
    const playwrightApi = new CustomPlaywrightApi();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'System',
            dataIndex: ['system', 'name'],
            key: 'system.name'
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Bearbeiten',
            key: 'edit',
            render: (text, record) => (
                <div>
                    <Link to={process.env.PUBLIC_URL + `/edit/test/${record.id}`} style={{marginRight: '8px'}}>
                        <EditOutlined style={{fontSize: '16px', color: '#1890ff'}}/>
                    </Link>
                    {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER, ROLES.GLOW_TEST_DESIGNER], keycloak) ? (
                        <Button
                            danger
                            onClick={() => handleDeleteButton(record)}
                            style={{marginRight: '8px', border: '0px'}}
                        >
                            <DeleteOutlined/>
                        </Button>
                    ) : (
                        <Button disabled style={{marginRight: '8px', border: '0px'}}>
                            <DeleteOutlined/>
                        </Button>
                    )}
                </div>
            ),
        },
        {
            title: 'Ausführen',
            key: 'play',
            width: '10%',
            render: (text, record) => (
                <div key={record.id} style={{display: 'flex', alignItems: 'center'}}>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined/>}
                        loading={isLoading === record.id} // Spinning-Symbol anzeigen, wenn isLoading true ist
                        onClick={() => handlePlayButtonClick(record.id)}
                    >
                    </Button>
                    <Tooltip title="Testausführung angucken (lokal)">
                        <Checkbox
                            style={{marginLeft: 8}}
                            onChange={(e) => handleHeadlessCheckboxChange(e.target.checked, record.id)}
                        >
                            <EyeOutlined style={{color: "red"}}/>
                        </Checkbox>
                    </Tooltip>
                    <Tooltip title="Aufzeichnung aktivieren/deaktivieren">
                        <Checkbox
                            style={{marginLeft: 8}}
                            onChange={(e) => handleRecordCheckboxChange(e.target.checked, record.id)}
                        >
                            <VideoCameraOutlined style={{color: "red"}}/>
                        </Checkbox>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Verzögern (ms)',
            dataIndex: 'delay',
            key: 'delay',
            width: '4%',
            render: (text, record) => (
                <Input
                    defaultValue={0}
                    value={delayValues[record.id] || 0}
                    onChange={(e) => handleDelayValues(record.id, e.target.value)}
                />
            ),
        },
        {
            title: 'Testläufe',
            dataIndex: 'testruns',
            key: 'testruns',
            width: '5%',
            render: (text, record) => (
                <Button danger onClick={() => {
                    setTestIdForRun(record.id);
                    setRunHistoryVisible(true);
                }} style={{marginRight: '8px', border: '0px', color: '#1890ff'}}>
                    <HistoryOutlined/>
                </Button>
            ),
        },
        {
            title: <DownloadOutlined/>,
            dataIndex: 'export',
            key: 'export',
            align: 'center' as AlignType,
            width: '3%',
            render: (text, record) => (
                <Checkbox onChange={(e) => updateCheckboxId(record.id)}></Checkbox>
            ),
        },
    ];

    const updateCheckboxId = (testId: number) => {
        // Erstelle eine Kopie des aktuellen Zustands
        let tests = [...selectedTests];

        // Überprüfe, ob die ID bereits im Array vorhanden ist
        const index = tests.indexOf(testId);

        if (index !== -1) {
            // Wenn die ID vorhanden ist, entferne sie
            tests.splice(index, 1);
        } else {
            // Wenn die ID nicht vorhanden ist, füge sie hinzu
            tests.push(testId);
        }

        // Aktualisiere den State mit dem neuen Array
        setSelectedTests(tests);
    }

    const doExportTests = () => {
        if (selectedTests.length > 0) {
            exportTestControllerApi.exportTestsByIds(selectedTests, headers)
                .then(response => {
                    downloadFile(response.data, "exported_tests", "json");
                    message.success("Datei erfolgreich heruntergeladen!");
                })
                .catch(error => {
                    message.error("Fehler beim Herunterladen einer Datei!");
                });
        } else {
            message.error("Es wurden keine Tests ausgewählt!");
        }
    };

    const fetchData = (page: number, size: number) => {
        const pageable: Pageable = {
            page: page - 1, // Spring verwendet 0-basierte Seitenzahlen
            size: size ? size : 10,
            sort: ['id;desc'] // Beispiel für Sortierung als Array
        } as Pageable;

        testControllerApi.getAllPageableTestsBySystem(system,
            pageable.size ? pageable.size : 10,
            pageable.page ? pageable.page : 0,
            pageable.sort ? pageable.sort : ['id;asc'],
            headers)
            .then((axiosResponse) => {
                let pageTestDto: PageTestDto = axiosResponse.data
                if (pageTestDto.content) {
                    const axiosData: Array<TestDto> = pageTestDto.content;
                    setTestData(axiosData);
                    if (pageTestDto.totalElements) {
                        setTotalItems(pageTestDto.totalElements);
                    }
                }
            })
            .catch((error) => {
                console.error('Fehler beim Abrufen der Daten:', error);
            });
    }

    useEffect(() => {
        fetchData(currentPage, pageSize);
    }, [system]);

    useEffect(() => {
        fetchData(currentPage, pageSize);
    }, [currentPage, pageSize]);

    // Funktion zum Filtern der Tabelle basierend auf dem Suchbegriff
    const filteredData = testData?.filter((test) =>
        test.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handlePlayButtonClick = (id) => {
        const executeVisible = headlessStates[id] || false;
        if (executeVisible) {
            executeLocalTest(id);
            return;
        }

        setLocalTestResult(false);
        setLocalTraceUrl(undefined);
        const executeWithTrace = recordingStates[id] || false;
        let executeTestRequest: ExecuteTestRequest = {delay: delayValues[id], executeWithTrace: executeWithTrace};
        setIsLoading(id);
        testExecutionControllerApi.executeTest(id, executeTestRequest, headers).then((axiosResponse) => {
                setIsLoading(null);
                const response = axiosResponse?.data;
                message.success('Test wurde ausgeführt!');
                if (response?.result) {
                    setTestResult(response.result);
                    setTestResultModalVisible(true);
                }
            }
        ).catch(error => message.error("Es ist ein Fehler bei der Ausführung aufgetreten!"))
    }

    const executeLocalTest = (id) => {
        setLocalTestResult(true);
        const executeWithTrace = recordingStates[id] || false;
        let request: TestRequest = {testId: id, jwtToken: keycloak.token ?? '', withTrace: executeWithTrace};
        playwrightApi.executeTest(request, headers)
            .then((axiosResponse) => {
                setIsLoading(null);
                const response: TestExecutionDto = axiosResponse?.data as TestExecutionDto;
                message.success('Test wurde ausgeführt!');
                if (response?.result) {
                    setTestResult(response.result);
                    setLocalTraceUrl(response.traceUrl);
                    setTestResultModalVisible(true);
                }
            })
            .catch((error) => {
                if (error.response?.data) {
                    message.error({content: error.response.data, duration: 30});
                }
                setIsLoading(null);
                if (axios.isAxiosError(error)) {
                    if (!error.response) {
                        message.error({
                            content: 'Bitte starte deine lokale GLOW-Anwendung, damit der Test ausgeführt werden kann!',
                            duration: 15
                        });
                    } else {
                        message.error('Es ist ein Fehler bei der Ausführung aufgetreten!');
                    }
                } else {
                    message.error('Es ist ein unerwarteter Fehler aufgetreten!');
                }
            });
    }

    const handleDeleteButton = (test: TestDto) => {
        setDeleteTest(test);
        setModalVisible(true);
    }

    const onModalCancel = () => {
        setModalVisible(false);
    }

    const handleDeleteTest = () => {
        if (deleteTest && deleteTest.id) {
            testControllerApi.deleteTest(deleteTest.id, headers).then(response => {
                    // Erfolgreiche Antwort
                    message.success(deleteTest.name + ' wurde erfolgreich gelöscht!');
                    setModalVisible(false);
                    fetchData(currentPage, pageSize);
                }
            ).catch((error) => {
                if (error.response?.data) {
                    let errorList: DeletionError[] = error.response.data;
                    setDeletionErrorList(errorList);
                    setDeletionModalVisible(true);
                } else {
                    message.error({content: "Es ist ein unerwarteter Fehler aufgetreten! ", duration: 30});
                }
            })
        }
    }

    const handleOnCloseTestResult = () => {
        setTestResultModalVisible(false);
    }

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const closeImportTestModal = () => {
        setIsModalForImportTestVisible(false);
        fetchData(currentPage, pageSize);
    }


    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }

    return (
        <div>
            <h1>Tests</h1>
            <Row justify="start" align="middle" gutter={[8, 8]} style={{marginBottom: '5px'}}>
                <Col span={19}>
                    <Input
                        placeholder="Nach Namen suchen"
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </Col>
                <Col span={2}>
                    <Button type="primary" style={{marginLeft: 50}} onClick={() => {
                        setIsModalForImportTestVisible(true)
                    }}>
                        <UploadOutlined/> Import
                    </Button>
                </Col>
                <Col span={2}>
                    <Button type="primary" style={{marginLeft: 50}} onClick={doExportTests}>
                        <DownloadOutlined/> Export
                    </Button>
                </Col>
            </Row>
            <Table
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    locale: {
                        items_per_page: "/ Seite"
                    }
                }}
                locale={{emptyText: 'Keine Daten vorhanden'}}
                onChange={handleTableChange}
                dataSource={filteredData} columns={columns}/>
            <Modal
                open={modalVisible}
                title={"Bist du dir sicher?"}
                onCancel={onModalCancel}
                footer={[
                    <Button key="cancel" onClick={onModalCancel}>
                        Nein
                    </Button>,
                    <Button key="save" danger onClick={handleDeleteTest}>
                        Ja
                    </Button>,
                ]}
            >
                <span>Willst du wirklich löschen: <b>{deleteTest?.name} ({deleteTest?.id})</b>?</span>
            </Modal>
            {testResult && !localTestResult &&
                <TestResultModal visible={testResultModalVisible} testResultId={testResult.id}
                                 onClose={handleOnCloseTestResult}/>}
            {testResult && localTestResult &&
                <TestResultModal visible={testResultModalVisible} testResult={testResult}
                                 onClose={handleOnCloseTestResult} traceUrl={localTraceUrl}/>}
            {testIdForRun &&
                <TestExecutionModal testId={testIdForRun} visible={runHistoryVisible}
                                    onClose={() => setRunHistoryVisible(false)}/>}
            <DeletionErrorModal errorList={deletionErrorList}
                                modalVisible={deletionModalVisible}
                                onClose={handleCloseDeletionModal}
                                errorMessage={errorMessage}/>
            <ImportTestModal visible={isModalForImportTestVisible} onClose={closeImportTestModal}/>
        </div>
    );
};

export default TestOverview;